import React, { useMemo, useState } from 'react';
import { setMajorChoice } from '../../services/api';
import { notifyProgress } from '../../services/progressEvent';

/**
 * Two slots — intended and backup — filled from the majors this school
 * ACTUALLY offers.
 *
 * Founder, 2026-08-05: "I think we should show two buttons for the user to
 * click - one the intended and one for back up.. that way we can clearly
 * state the major for each one."
 *
 * This is the structural fix for the Santa Clara defect. The model had
 * proposed "Applied Mathematics and Computer Science" as a backup at a
 * school whose catalog holds no such major; the name was stored verbatim,
 * could never be badged in the ranked list, and every guard downstream could
 * only report the miss after the fact. Here the options ARE the ranking's own
 * tiers, so an invented major is not expressible.
 *
 * The student clicking Save is the consent — the same path the Launchpad's
 * per-row buttons already take. No nonce, and the model never touches it.
 */

const TIER_LABEL = {
    strong: 'Strong match',
    possible: 'Possible',
    reach: 'Reach',
    long_shot: 'Long shot',
};

// Hoisted: defined in the render body it got a new component identity every
// render, remounting both nodes each time.
const Slot = ({ label, value, hint }) => (
    <div className="flex items-baseline gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#6B6B6B] w-16 shrink-0">
            {label}
        </span>
        <span className={value ? 'text-sm font-semibold text-[#2C2C2C]' : 'text-sm text-[#9A9A9A]'}>
            {value || hint}
        </span>
    </div>
);

const TIER_CLASS = {
    strong: 'bg-[#E7F3EA] text-[#1A4D2E]',
    possible: 'bg-[#EAF0FA] text-[#2A4A7A]',
    reach: 'bg-[#FDF3E0] text-[#8A5A00]',
    long_shot: 'bg-[#F3F0F0] text-[#6B6B6B]',
};

export default function MajorChoicePicker({ picker, email, onSaved, busy }) {
    const majors = useMemo(() => {
        const raw = Array.isArray(picker?.majors) ? picker.majors : [];
        const seen = new Set();
        const out = [];
        for (const m of raw) {
            const name = typeof m?.name === 'string' ? m.name.trim() : '';
            if (!name || seen.has(name)) continue;
            seen.add(name);
            out.push({ name, tier: m.tier, college: m.college });
        }
        return out;
    }, [picker]);

    // Seeded from the choice already saved at this school. This is
    // correctness, not polish: set_major_choice writes a COMPLETE choice
    // dict, so saving with the backup slot empty overwrites an existing
    // backup with null. Starting blank would let the student silently drop a
    // backup they set weeks ago — in the feature whose subject IS the backup.
    // Only seed names the picker can actually show, so a slot can never hold
    // something invisible the student cannot clear.
    const seed = (name) => (typeof name === 'string'
        && majors.some((m) => m.name === name) ? name : null);
    const [intended, setIntended] = useState(() => seed(picker?.current?.primary));
    const [backup, setBackup] = useState(() => seed(picker?.current?.backup));
    // A saved backup the picker CANNOT display (an unmatched legacy name —
    // the Santa Clara-era records). The empty slot then doesn't mean "the
    // student cleared it"; it means "we couldn't show it". Sending '' would
    // silently erase a decision they never saw — the implicit wipe this
    // whole contract exists to prevent. Absent (keep) is the honest signal;
    // the value stays visible and clearable on the Launchpad row, where it
    // actually renders.
    const invisibleSavedBackup = Boolean(picker?.current?.backup)
        && seed(picker?.current?.backup) === null;
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [savedNote, setSavedNote] = useState(null);

    if (!majors.length || !picker?.university_id) return null;

    // A backup that repeats the intended is not a second door — the whole
    // point is somewhere else to land if the first is a stretch.
    const pick = (slot) => (name) => {
        setError(null);
        if (slot === 'intended') {
            setIntended((cur) => (cur === name ? null : name));
            setBackup((cur) => (cur === name ? null : cur));
        } else {
            setBackup((cur) => (cur === name ? null : name));
            setIntended((cur) => (cur === name ? null : cur));
        }
    };

    const save = async () => {
        if (!intended || saving) return;
        setSaving(true);
        setError(null);
        try {
            // '' not undefined: absent now means KEEP the saved backup, so
            // an empty slot the student deliberately cleared has to say so
            // explicitly. This panel always shows the current state, so what
            // is on screen is what gets written.
            const res = await setMajorChoice(email, picker.university_id, intended,
                { backupMajor: backup || (invisibleSavedBackup ? undefined : '') });
            if (res?.success) {
                setDone(true);
                setSavedNote(res.note || null);
                notifyProgress();          // the school's read is now stale
                onSaved?.({ intended, backup, note: res.note || null });
            } else {
                setError(res?.error || "That didn't save — try again.");
            }
        } catch {
            setError("That didn't save — try again.");
        }
        setSaving(false);
    };

    if (done) {
        return (
            <div className="mt-2 rounded-xl border border-[#D8E6DC] bg-[#F4F9F5] px-3 py-2 text-sm text-[#1A4D2E]"
                data-testid="major-choice-picker-done">
                Saved — <span className="font-semibold">{intended}</span> as your intended
                {backup ? <> and <span className="font-semibold">{backup}</span> as your backup</> : null}.
                {/* The names here are canonical KB majors, so the server
                    should never object — but if it does, the student hears
                    it rather than the panel claiming a clean save. */}
                {savedNote && (
                    <span className="mt-1 block text-xs text-[#8A5A00]"
                        data-testid="major-choice-picker-note">{savedNote}</span>
                )}
            </div>
        );
    }

    return (
        <div className="mt-2 rounded-xl border border-[#E0DED8] bg-white p-3"
            data-testid="major-choice-picker">
            <p className="text-sm font-semibold text-[#2C2C2C]">
                Set your majors{picker.university_name ? ` at ${picker.university_name}` : ''}
            </p>
            <p className="mt-0.5 text-xs text-[#6B6B6B]">
                Pick the one you&apos;d actually apply with, and a second door if the first is a stretch.
            </p>

            <div className="mt-2 space-y-1 rounded-lg bg-[#F8F6F0] px-2.5 py-2">
                <Slot label="Intended" value={intended} hint="choose below" />
                <Slot label="Backup" value={backup} hint="optional" />
            </div>

            <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-[#F0EEE8]">
                {majors.map((m) => (
                    <li key={m.name} className="py-1.5">
                        <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm text-[#2C2C2C]">{m.name}</p>
                                {m.college && (
                                    <p className="truncate text-[11px] text-[#8A8A8A]">{m.college}</p>
                                )}
                            </div>
                            {m.tier && TIER_LABEL[m.tier] && (
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${TIER_CLASS[m.tier] || ''}`}>
                                    {TIER_LABEL[m.tier]}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => pick('intended')(m.name)}
                                aria-pressed={intended === m.name}
                                disabled={busy || saving}
                                className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                                    intended === m.name
                                        ? 'bg-[#1A4D2E] text-white'
                                        : 'border border-[#D8D5CE] text-[#4A4A4A] hover:bg-[#F3F1EC]'}`}
                            >
                                Intended
                            </button>
                            <button
                                type="button"
                                onClick={() => pick('backup')(m.name)}
                                aria-pressed={backup === m.name}
                                disabled={busy || saving}
                                className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                                    backup === m.name
                                        ? 'bg-[#2C2C2C] text-white'
                                        : 'border border-[#D8D5CE] text-[#4A4A4A] hover:bg-[#F3F1EC]'}`}
                            >
                                Backup
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            {error && <p className="mt-2 text-xs text-[#B54708]" data-testid="major-choice-picker-error">{error}</p>}

            <div className="mt-2 flex items-center gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={!intended || saving || busy}
                    data-testid="major-choice-picker-save"
                    className="rounded-lg bg-[#1A4D2E] px-3 py-1.5 text-xs font-semibold text-white
                        transition-opacity disabled:opacity-40"
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {!intended && (
                    <span className="text-[11px] text-[#8A8A8A]">Choose an intended major to save.</span>
                )}
            </div>
        </div>
    );
}
